import type {ReactNode} from "react";

import type {Components} from "react-markdown";
import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";

const heading = "text-base font-semibold mt-3 mb-1";

/**
 * Tailwind-Preflight ist aus (siehe `tailwind.config.js`), die Elemente tragen also Browser-Defaults; die Klassen hier
 * passen Abstände und Schriftgrößen an eine Chat-Blase an. `node` (der hast-Knoten) wird nicht ans DOM durchgereicht;
 * `props` stehen vor `className`, damit hast-Klassen wie `contains-task-list` oder `language-*` sie nicht ersetzen.
 */
const components: Components = {
    h1: ({node: _node, ...props}) => <h3 {...props} className="text-lg font-semibold mt-3 mb-1" />,
    h2: ({node: _node, ...props}) => <h4 {...props} className="text-lg font-semibold mt-3 mb-1" />,
    h3: ({node: _node, ...props}) => <h5 {...props} className={heading} />,
    h4: ({node: _node, ...props}) => <h6 {...props} className={heading} />,
    h5: ({node: _node, ...props}) => <h6 {...props} className={heading} />,
    h6: ({node: _node, ...props}) => <h6 {...props} className={heading} />,
    p: ({node: _node, ...props}) => <p {...props} className="my-2" />,
    ul: ({node: _node, ...props}) => <ul {...props} className="my-2 pl-5" />,
    ol: ({node: _node, ...props}) => <ol {...props} className="my-2 pl-5" />,
    hr: ({node: _node, ...props}) => <hr {...props} className="my-3 border-0 border-t border-surface-300" />,
    a: ({node: _node, ...props}) => (
        <a {...props} className="text-primary underline" target="_blank" rel="noopener noreferrer" />
    ),
    blockquote: ({node: _node, ...props}) => (
        <blockquote {...props} className="my-2 mx-0 border-0 border-l-4 border-solid border-surface-300 pl-3" />
    ),
    code: ({node: _node, ...props}) => <code {...props} className="px-1 rounded bg-surface-200 font-mono text-sm" />,
    // Codeblöcke: der Hintergrund sitzt am <pre>, das <code> darin bekommt keinen eigenen.
    pre: ({node: _node, ...props}) => (
        <pre
            {...props}
            className="my-2 p-3 rounded overflow-x-auto bg-surface-200 text-sm [&_code]:p-0 [&_code]:bg-transparent"
        />
    ),
    table: ({node: _node, ...props}) => (
        <div className="my-2 overflow-x-auto">
            <table {...props} className="border-collapse" />
        </div>
    ),
    th: ({node: _node, ...props}) => (
        <th {...props} className="border border-solid border-surface-300 px-2 py-1 text-left font-semibold" />
    ),
    td: ({node: _node, ...props}) => <td {...props} className="border border-solid border-surface-300 px-2 py-1" />,
};

interface ChatMarkdownProps {
    /** Markdown-Text (GFM) der KI. */
    text: string;
    /** Wird hinter dem gerenderten Markdown angehängt, z.B. ein Hinweis auf eine abgeschnittene Antwort. */
    children?: ReactNode;
}

/**
 * Rendert eine Antwort der KI als Markdown (GFM, einfache Zeilenumbrüche bleiben erhalten). Rohes HTML im Text wird
 * verworfen (`skipHtml`), gerendert wird ausschließlich über React-Elemente – die KI-Ausgabe kann so kein Markup oder
 * Script einschleusen.
 */
export function ChatMarkdown({text, children}: ChatMarkdownProps) {
    return (
        <div className="[&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
            <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]} skipHtml components={components}>
                {text}
            </ReactMarkdown>
            {children}
        </div>
    );
}
